// LayoutStaff.jsx - Version corrigée
import React, { useState, useRef, useEffect, useLayoutEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { 
  Home, Users, Settings, Database, Activity, Shield, 
  Code, Book, Webhook, Folder, Briefcase, FileText, 
  HelpCircle, Upload, CreditCard, LogOut, UserCircle,
  Key, Info, ChevronDown, Cpu, Server, ChevronRight
} from 'lucide-react';
import { NAVIGATION_MAP } from '../components/navigationConfig';
import { useMenu } from '../hooks/useMenu';
import RoleSwitcher from '../components/RoleSwitcher';
import '../styles/lmsiss.css';

// Composant pour gérer les icônes dynamiquement
const IconComponent = ({ name, className }) => {
  const icons = {
    home: <Home className={className} size={18} />,
    users: <Users className={className} size={18} />,
    settings: <Settings className={className} size={18} />,
    database: <Database className={className} size={18} />,
    activity: <Activity className={className} size={18} />,
    shield: <Shield className={className} size={18} />,
    code: <Code className={className} size={18} />,
    book: <Book className={className} size={18} />,
    webhook: <Webhook className={className} size={18} />,
    folder: <Folder className={className} size={18} />,
    briefcase: <Briefcase className={className} size={18} />,
    file: <FileText className={className} size={18} />,
    'file-text': <FileText className={className} size={18} />,
    help: <HelpCircle className={className} size={18} />,
    upload: <Upload className={className} size={18} />,
    'credit-card': <CreditCard className={className} size={18} />,
    cpu: <Cpu className={className} size={18} />,
    server: <Server className={className} size={18} />,
  };
  return icons[name] || <div className="w-[18px]" />;
};

// Composant Dropdown pour les sous-menus - Version corrigée
const NavDropdown = ({ item, location }) => {
  const { openMenus, toggleMenu, closeMenu } = useMenu();
  const dropdownRef = useRef(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 'auto', left: 'auto' });

  const isChildActive = item.children?.some(child => location.pathname === child.path) || false;
  const isOpen = openMenus[item.label] || false;

  // Calcul de la position du dropdown avec useLayoutEffect
  useLayoutEffect(() => {
    if (isOpen && dropdownRef.current) {
      const rect = dropdownRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + 4,
        left: rect.left,
      });
    }
  }, [isOpen]);

  // Gestion de la fermeture au clic extérieur
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        closeMenu(item.label);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [item.label, closeMenu]);

  const handleToggle = useCallback((e) => {
    e.stopPropagation();
    toggleMenu(item.label);
  }, [item.label, toggleMenu]);

  return (
    <div className="relative inline-block" ref={dropdownRef}>
      {/* Bouton du menu parent */}
      <button 
        onClick={handleToggle}
        className={`flex items-center gap-1 md:gap-2 px-3 md:px-4 py-1.5 rounded-md text-xs md:text-sm font-serif font-bold uppercase tracking-wider transition-all duration-200 whitespace-nowrap ${
          isOpen || isChildActive
            ? 'bg-[#009640] text-white shadow-md' 
            : 'text-[#009640] hover:bg-[#009640] hover:text-white hover:bg-opacity-10'
        }`}
      >
        <IconComponent 
          name={item.icon} 
          className={isOpen || isChildActive ? "text-white" : "text-[#009640]"} 
        />
        <span>{item.label}</span>
        <ChevronDown 
          size={14} 
          className={`transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} 
        />
      </button>

      {/* Menu déroulant des sous-menus - positionné au-dessus de tout */}
      {isOpen && (
        <div 
          className="fixed bg-white rounded-xl shadow-2xl border-2 border-[#ef7c00] min-w-[240px] py-2"
          style={{ 
            zIndex: 99999,
            top: dropdownPosition.top,
            left: dropdownPosition.left,
          }}
        >
          {/* En-tête du sous-menu */}
          <div className="px-4 py-1.5 mb-1 border-b-2 border-[#ef7c00]">
            <p className="text-[10px] font-serif font-bold text-[#009640] uppercase tracking-wider">
              {item.label}
            </p>
          </div>
          
          {/* Liste des sous-menus */}
          {item.children.map((child) => {
            const isChildActive = location.pathname === child.path;
            if (!child.path && child.label) {
              return (
                <div key={child.label} className="px-4 py-4 text-[9px] font-serif font-bold text-[#adb5bd] uppercase tracking-wider border-t border-[#f1f3f5] mt-2 pt-2">
                  {child.label}
                </div>
              );
            }
            return (
              <Link
                key={child.path}
                to={child.path}
                className={`flex items-center gap-3 px-4 py-2.5 text-sm font-serif transition-all duration-150 ${
                  isChildActive 
                    ? 'bg-[#e8f5ed] text-[#009640] font-bold' 
                    : 'text-[#495057] hover:bg-[#f8f9fa] hover:text-[#212529]'
                }`}
              >
                <IconComponent 
                  name={child.icon} 
                  className={isChildActive ? "text-[#009640]" : "text-[#6c757d]"} 
                />
                <span className="lowercase">{child.label}</span>
                {isChildActive && (
                  <ChevronRight size={14} className="ml-auto text-[#009640]" />
                )}
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default function LayoutStaff({ children, onLogout, role, username }) {
  const location = useLocation();
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const profileRef = useRef(null);
  
  const menuItems = NAVIGATION_MAP[role] || [];

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileRef.current && !profileRef.current.contains(event.target)) {
        setIsProfileOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="min-h-screen bg-[#f8f9fa] flex flex-col font-serif">
      {/* TOP BAR */}
      <header className="bg-white border-b-2 border-[#ef7c00] px-6 md:px-8 h-14 flex items-center justify-between shadow-md z-50 relative">
        <div className="flex items-center gap-3 md:gap-4 h-14">
          <h1 className="text-lg md:text-xl font-serif font-black text-[#009640] tracking-tighter italic uppercase flex items-center">
            <img 
              src="/images/logoiss.png" 
              alt="ISS Logo" 
              className="h-8 md:h-10 w-auto object-contain" 
            />
            LMS Portal - {role?.replace('ROLE_', '')}
          </h1>
        </div>

        {/* Sélecteur de rôle actif (masqué automatiquement si le compte n'a qu'un rôle) */}
        <div className="flex items-center gap-3">
          <RoleSwitcher mode="compact" />

        {/* Profil */}
        <div className="relative flex items-center h-14" ref={profileRef}>
          <button 
            onClick={() => setIsProfileOpen(!isProfileOpen)}
            className="flex items-center gap-2 px-2 h-14 hover:bg-[#f8f9fa] rounded-lg transition-colors"
          >
            <div className="w-8 h-8 rounded-full bg-[#009640] flex items-center justify-center text-white font-bold text-sm font-serif">
              {username?.substring(0, 2).toUpperCase() || 'AD'}
            </div>
            <div className="hidden md:block">
              <span className="text-sm font-serif font-bold text-[#212529]">{username || 'Utilisateur'}</span>
            </div>
            <ChevronDown size={14} className={`text-[#6c757d] transition-transform ${isProfileOpen ? 'rotate-180' : ''}`} />
          </button>

          {isProfileOpen && (
            <div 
              className="absolute right-0 top-full mt-1 bg-white rounded-xl shadow-2xl border-2 border-[#ef7c00] min-w-[200px] py-2"
              style={{ zIndex: 99999 }}
            >
              <div className="px-4 py-2 border-b-2 border-[#ef7c00]">
                <p className="text-[10px] font-serif font-bold text-[#009640] uppercase tracking-wider">Mon Compte</p>
              </div>
              <Link to="/profile" className="flex items-center gap-3 px-4 py-2.5 text-sm font-serif text-[#495057] hover:bg-[#f8f9fa] transition-colors">
                <UserCircle size={16} className="text-[#6c757d]" />
                <span>Profil & Préférences</span>
              </Link>
              <div className="border-t border-[#f1f3f5] my-1" />
              <button onClick={onLogout} className="w-full flex items-center gap-3 px-4 py-2.5 text-sm font-serif text-[#ef7c00] hover:bg-[#fef3e8] transition-colors font-bold">
                <LogOut size={16} />
                <span>Déconnexion</span>
              </button>
            </div>
          )}
        </div>
        </div>
      </header>

      {/* NAVIGATION BAR */}
      <nav className="bg-[#f1f3f5] shadow-sm sticky top-0 border-b-4 border-[#ef7c00] z-40">
        <div className="max-w-full px-4 md:px-8 flex h-12 items-center overflow-x-auto">
          <div className="flex gap-1 md:gap-2 flex-nowrap">
            {menuItems.map((item) => {
              if (item.children && item.children.length > 0) {
                return <NavDropdown key={item.label} item={item} location={location} />;
              }

              const isActive = location.pathname === item.path;

              return (
                <Link 
                  key={item.path} 
                  to={item.path}
                  className={`flex items-center gap-1 md:gap-2 px-3 md:px-4 py-1.5 rounded-md text-xs md:text-sm font-serif font-bold uppercase tracking-wider transition-all duration-200 whitespace-nowrap ${
                    isActive 
                      ? 'bg-[#009640] text-white shadow-md' 
                      : 'text-[#009640] hover:bg-[#009640] hover:text-white hover:bg-opacity-10'
                  }`}
                >
                  <IconComponent 
                    name={item.icon} 
                    className={isActive ? "text-white" : "text-[#009640]"} 
                  />
                  <span className="hidden sm:inline">{item.label}</span>
                </Link>
              );
            })}
          </div>
        </div>
      </nav>

      {/* MAIN CONTENT */}
      <main className="flex-grow p-4 md:p-6" style={{ marginTop: 0 }}>
        <div className="">
          {children}
        </div>
      </main>
    </div>
  );
}